import { useEffect, useRef, useState } from 'react';
import type { EditorAsset } from '../../../utils/quickEditor';
import { loadWaveformPeaks } from '../../../utils/editorWaveform';
import { stableMediaKey } from '../../../utils/workspaceEdit';

/** 'reading' while the sound is decoded; 'none' when there is no sound that can be read. */
export type WaveformStatus = 'reading' | 'ready' | 'none';

export interface WaveformSource {
  /** The media's bytes, as loaded for the editor. */
  blob?: Blob;
  /** Its object URL, read when the bytes were not kept. */
  url?: string;
}

/** Long enough for an hour of sound; a decode that takes longer is given up so the next can start. */
const DECODE_TIMEOUT_MS = 60_000;

/**
 * Under what a waveform is kept: the media itself where it has a lasting name, else the
 * editor's own copy with its size (a relinked file gets the same key, but rarely the same size).
 */
const waveKey = (asset: EditorAsset, blob: Blob): string => {
  if (asset.source.type === 'ref') {
    const stable = stableMediaKey(asset.source.ref);
    if (stable) return stable;
  }
  return `${asset.source.type === 'local' ? asset.source.key : asset.id}:${blob.size}`;
};

const withTimeout = <T,>(work: Promise<T>, ms: number): Promise<T> =>
  Promise.race([work, new Promise<T>((_, reject) => setTimeout(() => reject(new Error('reading the sound took too long')), ms))]);

/**
 * The waveform of each asset that has a clip on an audio track, by asset id, once its
 * bytes are here. They are decoded one at a time (decoding takes memory), kept in this
 * browser, and their state is told too, so a clip can show that its sound is being read
 * or that it has none.
 */
export const useWaveforms = (
  assets: EditorAsset[],
  sources: Record<string, WaveformSource | undefined>,
  /** Assets with a clip on an audio track: only these are drawn, so only these are read. */
  wanted: Set<string>,
): { waves: Record<string, Float32Array>; status: Record<string, WaveformStatus> } => {
  const [waves, setWaves] = useState<Record<string, Float32Array>>({});
  const [status, setStatus] = useState<Record<string, WaveformStatus>>({});
  // The source each waveform was made from; a new one (a relink) makes a new waveform
  const started = useRef(new Map<string, WaveformSource>());
  const queue = useRef<Promise<void>>(Promise.resolve());
  // Set on every mount: React's StrictMode unmounts and mounts again once in development
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    for (const asset of assets) {
      const source = sources[asset.id];
      if (!source || (!source.blob && !source.url) || !wanted.has(asset.id)) continue;
      const previous = started.current.get(asset.id);
      if (previous && previous.blob === source.blob && previous.url === source.url) continue;
      started.current.set(asset.id, source);
      const current = () => alive.current && started.current.get(asset.id) === source;
      setStatus(prev => ({ ...prev, [asset.id]: 'reading' }));
      queue.current = queue.current.then(async () => {
        if (!current()) return;
        try {
          // The bytes are normally kept with the media; an older load only kept its URL
          const blob = source.blob ?? await (await fetch(source.url!)).blob();
          const peaks = await withTimeout(loadWaveformPeaks(waveKey(asset, blob), blob), DECODE_TIMEOUT_MS);
          if (!current()) return;
          setWaves(prev => ({ ...prev, [asset.id]: peaks }));
          setStatus(prev => ({ ...prev, [asset.id]: 'ready' }));
        } catch (e) {
          if (!current()) return;
          console.warn(`[Edit] No waveform for ${asset.name}:`, (e as Error)?.message || e);
          setWaves(prev => { const { [asset.id]: _gone, ...rest } = prev; return rest; });
          setStatus(prev => ({ ...prev, [asset.id]: 'none' }));
        }
      });
    }
  }, [assets, sources, wanted]);

  return { waves, status };
};
